import axios from 'axios';

const API_BASE = '/api/luxury-hire';

export interface LuxuryVehicle {
  id: number;
  uuid: string;
  name: string;
  brand: string;
  model?: string;
  year?: number;
  color?: string;
  vehicle_category: string;
  passenger_capacity: number;
  photos?: string[];
  thumbnail_url?: string;
  video_url?: string;
  features?: string[];
  description?: string;
  hourly_rate: number;
  half_day_rate?: number;
  daily_rate: number;
  currency: string;
  included_hours_half_day: number;
  included_hours_daily: number;
  included_km: number;
  extra_hour_rate?: number;
  extra_km_rate: number;
  security_required: boolean;
  security_fee: number;
  minimum_hours: number;
  advance_booking_days: number;
  same_day_booking: boolean;
  deposit_percentage: number;
  cancellation_hours: number;
  cancellation_fee_percentage: number;
  status: string;
  is_featured: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface LuxuryPackage {
  id: number;
  name: string;
  description?: string;
  vehicle_id?: number;
  vehicle_category?: string;
  duration_type: string;
  duration_hours?: number;
  duration_days?: number;
  base_price: number;
  currency: string;
  driver_included: boolean;
  security_included: boolean;
  included_km?: number;
  addons_included?: string[];
  extra_hour_rate?: number;
  extra_km_rate?: number;
  discount_percentage: number;
  is_active: boolean;
  valid_from?: string;
  valid_until?: string;
  created_at: string;
  updated_at: string;
}

export interface LuxuryAddon {
  id: number;
  name: string;
  description?: string;
  icon?: string;
  price: number;
  price_type: string;
  currency: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface LuxuryBooking {
  id: number;
  uuid: string;
  booking_reference: string;
  customer_id: number;
  customer_name: string;
  customer_phone: string;
  customer_email?: string;
  vehicle_id: number;
  vehicle_name: string;
  vehicle_category: string;
  package_id?: number;
  package_name?: string;
  booking_type: string;
  start_datetime: string;
  end_datetime: string;
  duration_hours: number;
  pickup_address: string;
  pickup_latitude?: number;
  pickup_longitude?: number;
  special_requests?: string;
  occasion?: string;
  addons?: any[];
  security_included: boolean;
  base_price: number;
  addons_total: number;
  surge_multiplier: number;
  surge_amount: number;
  discount_amount: number;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  currency: string;
  deposit_amount: number;
  deposit_paid: boolean;
  balance_amount: number;
  balance_paid: boolean;
  payment_status: string;
  driver_id?: number;
  driver_name?: string;
  driver_phone?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface BookingQuote {
  vehicle_id: number;
  vehicle_name: string;
  booking_type: string;
  duration_hours: number;
  base_price: number;
  addons_total: number;
  surge_multiplier: number;
  surge_amount: number;
  discount_amount: number;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  deposit_amount: number;
  balance_amount: number;
  currency: string;
  breakdown: any;
}

export interface VehicleListResponse {
  vehicles: LuxuryVehicle[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface BookingListResponse {
  bookings: LuxuryBooking[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const luxuryHireApi = {
  getVehicles: async (params?: {
    page?: number;
    per_page?: number;
    category?: string;
    brand?: string;
    status?: string;
    is_featured?: boolean;
    min_capacity?: number;
    search?: string;
  }): Promise<VehicleListResponse> => {
    const response = await axios.get(`${API_BASE}/vehicles`, { params });
    return response.data;
  },

  getVehicle: async (id: number): Promise<LuxuryVehicle> => {
    const response = await axios.get(`${API_BASE}/vehicles/${id}`);
    return response.data;
  },

  createVehicle: async (data: Partial<LuxuryVehicle>): Promise<LuxuryVehicle> => {
    const response = await axios.post(`${API_BASE}/vehicles`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  updateVehicle: async (id: number, data: Partial<LuxuryVehicle>): Promise<LuxuryVehicle> => {
    const response = await axios.put(`${API_BASE}/vehicles/${id}`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  deleteVehicle: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE}/vehicles/${id}`, {
      headers: getAuthHeaders(),
    });
  },

  getPackages: async (params?: {
    vehicle_id?: number;
    vehicle_category?: string;
    is_active?: boolean;
  }): Promise<LuxuryPackage[]> => {
    const response = await axios.get(`${API_BASE}/packages`, { params });
    return response.data;
  },

  createPackage: async (data: Partial<LuxuryPackage>): Promise<LuxuryPackage> => {
    const response = await axios.post(`${API_BASE}/packages`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  updatePackage: async (id: number, data: Partial<LuxuryPackage>): Promise<LuxuryPackage> => {
    const response = await axios.put(`${API_BASE}/packages/${id}`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  deletePackage: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE}/packages/${id}`, {
      headers: getAuthHeaders(),
    });
  },

  getAddons: async (is_active?: boolean): Promise<LuxuryAddon[]> => {
    const response = await axios.get(`${API_BASE}/addons`, {
      params: { is_active },
    });
    return response.data;
  },

  createAddon: async (data: Partial<LuxuryAddon>): Promise<LuxuryAddon> => {
    const response = await axios.post(`${API_BASE}/addons`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  updateAddon: async (id: number, data: Partial<LuxuryAddon>): Promise<LuxuryAddon> => {
    const response = await axios.put(`${API_BASE}/addons/${id}`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  deleteAddon: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE}/addons/${id}`, {
      headers: getAuthHeaders(),
    });
  },

  checkAvailability: async (data: {
    vehicle_id: number;
    start_datetime: string;
    end_datetime: string;
  }): Promise<{ vehicle_id: number; is_available: boolean; conflicts: any[]; message: string }> => {
    const response = await axios.post(`${API_BASE}/availability/check`, data);
    return response.data;
  },

  getQuote: async (data: {
    vehicle_id: number;
    package_id?: number;
    booking_type: string;
    start_datetime: string;
    end_datetime: string;
    addons?: number[];
    security_included?: boolean;
  }): Promise<BookingQuote> => {
    const response = await axios.post(`${API_BASE}/bookings/quote`, data);
    return response.data;
  },

  createBooking: async (data: {
    vehicle_id: number;
    package_id?: number;
    booking_type: string;
    start_datetime: string;
    end_datetime: string;
    pickup_address: string;
    pickup_latitude?: number;
    pickup_longitude?: number;
    special_requests?: string;
    occasion?: string;
    addons?: any[];
    security_included?: boolean;
  }): Promise<LuxuryBooking> => {
    const response = await axios.post(`${API_BASE}/bookings`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  getBookings: async (params?: {
    page?: number;
    per_page?: number;
    status?: string;
    vehicle_id?: number;
    customer_id?: number;
    start_date?: string;
    end_date?: string;
  }): Promise<BookingListResponse> => {
    const response = await axios.get(`${API_BASE}/bookings`, {
      params,
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  getBooking: async (id: number): Promise<LuxuryBooking> => {
    const response = await axios.get(`${API_BASE}/bookings/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  updateBookingStatus: async (id: number, data: {
    status: string;
    cancellation_reason?: string;
  }): Promise<LuxuryBooking> => {
    const response = await axios.put(`${API_BASE}/bookings/${id}/status`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  assignDriver: async (id: number, driver_id: number): Promise<LuxuryBooking> => {
    const response = await axios.put(
      `${API_BASE}/bookings/${id}/assign-driver`,
      { driver_id },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  getDashboardStats: async (): Promise<{
    vehicles: { total: number; available: number; in_use: number };
    bookings: { total: number; pending: number; confirmed: number; today: number };
    revenue: { total: number; currency: string };
  }> => {
    const response = await axios.get(`${API_BASE}/stats/dashboard`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  getSettings: async (): Promise<any> => {
    const response = await axios.get(`${API_BASE}/settings`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  updateSettings: async (data: any): Promise<any> => {
    const response = await axios.put(`${API_BASE}/settings`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  getAvailabilityBlocks: async (params?: {
    vehicle_id?: number;
    start_date?: string;
    end_date?: string;
  }): Promise<any[]> => {
    const response = await axios.get(`${API_BASE}/availability/blocks`, {
      params,
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  createAvailabilityBlock: async (data: {
    vehicle_id: number;
    start_datetime: string;
    end_datetime: string;
    block_type: string;
    reason?: string;
  }): Promise<any> => {
    const response = await axios.post(`${API_BASE}/availability/blocks`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  deleteAvailabilityBlock: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE}/availability/blocks/${id}`, {
      headers: getAuthHeaders(),
    });
  },

  getSurgePricing: async (): Promise<any[]> => {
    const response = await axios.get(`${API_BASE}/surge-pricing`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  createSurgePricing: async (data: any): Promise<any> => {
    const response = await axios.post(`${API_BASE}/surge-pricing`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  deleteSurgePricing: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE}/surge-pricing/${id}`, {
      headers: getAuthHeaders(),
    });
  },

  initiatePayment: async (
    bookingId: number,
    paymentType: 'deposit' | 'balance' | 'full' = 'deposit',
    gateway: 'paystack' | 'seerbit' = 'paystack'
  ): Promise<{
    success: boolean;
    gateway: string;
    payment_url: string;
    reference: string;
    amount: number;
    currency: string;
  }> => {
    const response = await axios.post(
      `${API_BASE}/bookings/${bookingId}/initiate-payment`,
      null,
      {
        params: { payment_type: paymentType, payment_gateway: gateway },
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  },
};

export default luxuryHireApi;
